/**
 * text-complete-client — 瀏覽器端輕量「文字 → 文字」LLM 呼叫。
 *
 * 用途:錯題本「會教」功能 —— 生更詳細的步驟說明、為沒有產生器的科目/KP 生一題類似題。
 *
 * Provider 順序(同 `exam-upload/ocr-client.ts` 慣例):
 *   1. local Claude bridge(`resolveBridgeUrl()` → `POST {bridge}/complete`,
 *      用老闆自己的 Claude 訂閱,品質最好;`worker/src/ai-provider.ts` 的
 *      `tryBridgeComplete` 也是打這支)。
 *   2. 無 bridge → 回 null(呼叫端自行 fallback 顯示既有說明,不阻斷 UI)。
 *
 * 設計鐵則:純學習側、best-effort、絕不 throw、絕不碰戰鬥/commanderXp。
 */

import { resolveBridgeUrl } from '../shared/bridge-resolver';

/** Bridge `/complete` 回應形狀(與 worker `tryBridgeComplete` 一致)。 */
interface CompleteResponse {
  text?: unknown;
}

/**
 * 等 bridge `/complete` 多久。實測單發 ~20s,多發平行(變形卷)時單機 bridge
 * 排隊會拉到 30-40s+,45s 在壓力下整批逾時 →「生不出變形題」(2026-06-11)。
 * 呼叫端已用併發池限流,這裡給足 90s 當保險。
 */
const COMPLETE_TIMEOUT_MS = 90_000;

/**
 * 呼叫 LLM 做文字補全。成功回 trim 後字串;任何失敗(無 bridge / 非 2xx /
 * 逾時 / 壞 JSON / 空字串)都回 `null`,呼叫端 fallback。永不 throw。
 */
export async function textComplete(opts: {
  system?: string;
  prompt: string;
}): Promise<string | null> {
  const bridge = await resolveBridgeUrl();
  if (!bridge) return null;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), COMPLETE_TIMEOUT_MS);
  try {
    const res = await fetch(`${bridge.replace(/\/$/, '')}/complete`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ system: opts.system, prompt: opts.prompt }),
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as CompleteResponse;
    if (typeof data.text === 'string' && data.text.trim().length > 0) {
      return data.text.trim();
    }
    return null;
  } catch {
    return null; // unreachable / aborted / bad shape → 呼叫端 fallback。
  } finally {
    clearTimeout(timer);
  }
}
